/**
 * AddFromPoolModal organism — right-side Sheet listing pool talents ranked by
 * mock match score, reusing the talent-pool ROW elements the recruiter already
 * knows from the pool table.
 *
 * Each row shows:
 *   - a select checkbox (multi-select + select-all in the header),
 *   - the TalentRow identity (avatar + name + email),
 *   - the talent's skills with the JOB's required skills highlighted,
 *   - a 3-level qualitative match badge (Perfect / Good / Partial),
 *   - a "see more" toggle that expands the numeric MatchBreakdown (collapsed by default).
 *
 * A Sheet.Footer hosts a bulk "Add selected (N)" action that assigns every selected
 * talent to the job at once. Already-assigned talents are shown disabled and are not
 * selectable.
 *
 * The job's required skills (for highlighting + the 3-level mapping context) are
 * sourced from useJobs() matched on jobId — the only job identifier the sheet has.
 *
 * No hardcoded strings, no bg-gradient-*, no emojis.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Eye, Clock } from 'lucide-react';
import { Sheet } from '../Sheet/Sheet';
import { Button } from '@/components/ui/atoms/Button';
import { Tag } from '../../atoms/Tag';
import { SkillChip } from '../../atoms/SkillChip';
import { SearchInput } from '../../search-input';
import { Skeleton } from '../../atoms/Skeleton';
import { Typography } from '../../atoms/Typography';
import { TalentRow } from '@/components/talent-pool/TalentRow';
import { MatchBreakdown } from '@/components/talent-pool/MatchBreakdown';
import {
  getMatchLevelTier,
  buildRequiredSkillSet,
  isSkillMatched,
} from '@/components/talent-pool/matchLevel.utils';

import { useJobs, useMatchSuggestions } from '@/hooks/useTalentPool';
import { useBulkAssignToJob } from '@/hooks/useTalentMutations';
import type {
  TalentMatchSuggestion,
  TalentSkill,
} from '@/types/talent.types';

// ── Skeleton row ─────────────────────────────────────────────────────────────

const PoolRowSkeleton = () => (
  <div className="flex items-center gap-3 px-2 py-3 border-b border-light-lila/30">
    <Skeleton className="h-4 w-4 rounded shrink-0" />
    <Skeleton className="h-6 w-6 rounded shrink-0" />
    <Skeleton className="h-8 w-8 rounded-full shrink-0" />
    <div className="flex-1 flex flex-col gap-1">
      <Skeleton className="h-4 w-36" />
      <Skeleton className="h-3 w-24" />
    </div>
    <Skeleton className="h-6 w-24 rounded-full shrink-0" />
  </div>
);

// ── Skill chips with required-skill highlighting ──────────────────────────────

interface HighlightedSkillsProps {
  skills: TalentSkill[];
  requiredSet: Set<string>;
  /** Show every skill instead of capping at 6 with a "+N" badge. */
  showAll?: boolean;
}

/**
 * Render the talent's skills as small chips, emphasizing the ones that intersect
 * the job's required skills (matched = brand/purple, others = muted/neutral).
 * Matched skills are sorted first so they read at a glance.
 */
const HighlightedSkills: React.FC<HighlightedSkillsProps> = ({ skills, requiredSet, showAll }) => {
  const { t } = useTranslation('talentPool');

  const ordered = useMemo(() => {
    const matched: TalentSkill[] = [];
    const rest: TalentSkill[] = [];
    for (const s of skills) {
      (isSkillMatched(s.name, requiredSet) ? matched : rest).push(s);
    }
    return [...matched, ...rest];
  }, [skills, requiredSet]);

  const MAX_VISIBLE = showAll ? ordered.length : 6;
  const visible = ordered.slice(0, MAX_VISIBLE);
  const overflow = ordered.length - visible.length;

  if (ordered.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 mt-1.5">
      {visible.map((skill) => {
        const matched = isSkillMatched(skill.name, requiredSet);
        return (
          <SkillChip
            key={skill.name}
            label={skill.name}
            tone={matched ? 'primary' : 'info'}
            className={matched ? 'ring-1 ring-purple font-semibold' : undefined}
            title={matched ? t('addFromPool.matchedSkillsAria') : undefined}
            aria-label={matched ? t('addFromPool.matchedSkillsAria') : undefined}
          />
        );
      })}
      {overflow > 0 && (
        <Tag intent="secondary" variant="subtle">
          +{overflow}
        </Tag>
      )}
    </div>
  );
};

// ── Per-talent row ────────────────────────────────────────────────────────────

interface ScopedTalentRowProps {
  /** Ranked match entry — carries the profile info AND the embedded breakdown. */
  entry: TalentMatchSuggestion;
  requiredSet: Set<string>;
  /** Whether this row is selected for the bulk add. */
  selected: boolean;
  /** Toggle selection for this row. */
  onToggleSelect: () => void;
}

const ScopedTalentRow: React.FC<ScopedTalentRowProps> = ({
  entry,
  requiredSet,
  selected,
  onToggleSelect,
}) => {
  const { t } = useTranslation('talentPool');
  const [expanded, setExpanded] = useState(false);

  const { breakdown } = entry;
  const tier = getMatchLevelTier(entry.matchScore);

  /** Derive two-letter initials from full name */
  const initials = useMemo(() => {
    const parts = entry.fullName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [entry.fullName]);

  // Prefer the full skill list (topSkills is display-only first 3).
  const skills = entry.allSkills.length > 0 ? entry.allSkills : entry.topSkills;

  // Years of experience decomposed into years + months (Meta CV style).
  const yoeLabel = useMemo(() => {
    const totalMonths = Math.round(Math.max(0, entry.totalYoe ?? 0) * 12);
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    const parts: string[] = [];
    if (years > 0) parts.push(`${years} ${t('pool.yoeSuffix')}`);
    if (months > 0) parts.push(`${months} ${t('addFromPool.monthsSuffix')}`);
    return parts.length ? parts.join(' ') : `0 ${t('pool.yoeSuffix')}`;
  }, [entry.totalYoe, t]);

  return (
    <div className="border-b border-light-lila/30 last:border-0">
      {/* Row header */}
      <div className="flex items-start gap-2 px-2 py-3">
        {/* Select checkbox */}
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={t('addFromPool.selectRow')}
          className="mt-1 shrink-0 h-4 w-4 rounded border-light-lila text-purple accent-purple cursor-pointer"
        />

        {/* Expand chevron */}
        <Button
          type="icon"
          intent="muted"
          size="xs"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-label={expanded ? t('addFromPool.seeLess') : t('addFromPool.seeMore')}
          title={expanded ? t('addFromPool.seeLess') : t('addFromPool.seeMore')}
          icon={expanded ? <ChevronDown /> : <ChevronRight />}
          className="mt-0.5 shrink-0"
        />

        {/* Identity + highlighted skills */}
        <div className="flex-1 min-w-0">
          <TalentRow
            fullName={entry.fullName}
            initials={initials}
            avatarUrl={entry.avatarUrl}
            secondary={entry.email}
            avatarSize="sm"
          />
          <HighlightedSkills skills={skills} requiredSet={requiredSet} showAll={expanded} />
        </div>

        {/* Open the full talent profile in a new tab + qualitative match badge */}
        <div className="flex shrink-0 items-center gap-1.5 mt-0.5">
          <a
            href={`/talent-pool/${entry.profileId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={t('addFromPool.viewProfile')}
            title={t('addFromPool.viewProfile')}
            className="flex h-7 w-7 items-center justify-center rounded-md text-dark-invirtus/45 hover:bg-dark-invirtus-soft hover:text-dark-invirtus transition-colors"
          >
            <Eye className="h-4 w-4" />
          </a>
          <Tag intent={tier.intent} variant="subtle" className="whitespace-nowrap">
            {t(tier.labelKey as never)}
          </Tag>
        </div>
      </div>

      {/* Expandable details: numeric breakdown + years-of-experience quick fact. */}
      {expanded && (
        <div className="pl-14 pr-2 pb-3">
          <MatchBreakdown breakdown={breakdown} />
          <div className="mt-2 flex justify-end">
            <span className="inline-flex items-center gap-1.5 font-body text-xs text-dark-invirtus/60">
              <Clock className="h-3.5 w-3.5 text-dark-invirtus/40" />
              {yoeLabel}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

export interface AddFromPoolModalProps {
  /** Whether the panel is open */
  open: boolean;
  /** Job to match against and assign into */
  jobId: string;
  /** Called when the panel should close */
  onClose: () => void;
  /** Optional callback after talents have been successfully added */
  onAdded?: () => void;
}

export const AddFromPoolModal: React.FC<AddFromPoolModalProps> = ({
  open,
  jobId,
  onClose,
  onAdded,
}) => {
  const { t } = useTranslation('talentPool');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Debounce the server-side search so typing does not fire a request per keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMatchSuggestions(
    jobId,
    { search: debouncedSearch, excludeAssigned: true },
    { enabled: open },
  );
  const { data: jobs } = useJobs();
  const bulkAssign = useBulkAssignToJob(jobId);

  // Flatten the ranked pages — already sorted highest→lowest by the backend.
  const entries = useMemo<TalentMatchSuggestion[]>(
    () => (data?.pages ?? []).flatMap((p) => p.items),
    [data],
  );
  // First page carries the counts used to distinguish the empty states.
  const total = data?.pages[0]?.total ?? 0;
  const poolTotal = data?.pages[0]?.poolTotal ?? 0;

  // Required-skill set for this job (accent-insensitive) — drives skill highlighting.
  const requiredSet = useMemo(() => {
    const job = jobs?.find((j) => j.id === jobId);
    return buildRequiredSkillSet(job?.requiredSkills ?? []);
  }, [jobs, jobId]);

  // Every loaded row is selectable (assigned talents are excluded server-side).
  const loadedIds = useMemo(() => entries.map((e) => e.profileId), [entries]);
  const allSelected =
    loadedIds.length > 0 && loadedIds.every((id) => selectedIds.has(id));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (loadedIds.every((id) => prev.has(id))) {
        const next = new Set(prev);
        loadedIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...loadedIds]);
    });
  };

  // Infinite scroll — load the next ranked page as the sentinel nears the viewport.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (records) => {
        if (records[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, entries.length]);

  const handleClose = () => {
    setSearch('');
    setDebouncedSearch('');
    setSelectedIds(new Set());
    onClose();
  };

  const handleAddSelected = () => {
    const talentIds = [...selectedIds];
    if (talentIds.length === 0) return;
    bulkAssign.mutate(
      { talentIds },
      {
        onSuccess: () => {
          onAdded?.();
          handleClose();
        },
      },
    );
  };

  const selectedCount = selectedIds.size;
  const isEmpty = !isLoading && entries.length === 0;
  // "Everyone is already on the job" vs. "the pool itself is empty".
  const allAssigned = isEmpty && !debouncedSearch && total === 0 && poolTotal > 0;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }} size="md">
      <Sheet.Header>
        <Sheet.Title>{t('addFromPool.title')}</Sheet.Title>
      </Sheet.Header>

      <Sheet.Body className="flex flex-col gap-3 !py-3">
        {/* Search */}
        <div className="px-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('addFromPool.searchPlaceholder')}
            showClearButton
          />
        </div>

        {/* Select-all header — only when there are rows to select */}
        {!isLoading && entries.length > 0 && (
          <label className="flex items-center gap-2 px-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              aria-label={t('addFromPool.selectAll')}
              className="h-4 w-4 rounded border-light-lila text-purple accent-purple cursor-pointer"
            />
            <Typography variant="body-sm" color="secondary">
              {t('addFromPool.selectAll')}
            </Typography>
          </label>
        )}

        {/* Loading skeleton — 5 rows */}
        {isLoading && (
          <div className="flex flex-col">
            {Array.from({ length: 5 }).map((_, i) => (
              <PoolRowSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Ranked talent rows (highest → lowest match), embedded breakdowns */}
        {!isLoading && (
          <div className="flex flex-col">
            {isEmpty && (
              <div className="py-8 text-center px-6">
                <Typography variant="body-sm" color="secondary">
                  {allAssigned
                    ? t('addFromPool.allAssignedHeading')
                    : t('pool.emptyHeading')}
                </Typography>
                {allAssigned && (
                  <Typography variant="body-sm" color="secondary" className="mt-1 opacity-70">
                    {t('addFromPool.allAssignedBody')}
                  </Typography>
                )}
              </div>
            )}
            {entries.map((entry) => (
              <ScopedTalentRow
                key={entry.profileId}
                entry={entry}
                requiredSet={requiredSet}
                selected={selectedIds.has(entry.profileId)}
                onToggleSelect={() => toggleSelect(entry.profileId)}
              />
            ))}

            {/* Infinite-scroll sentinel + next-page spinner */}
            {hasNextPage && (
              <div ref={sentinelRef} className="py-3">
                {isFetchingNextPage && <PoolRowSkeleton />}
              </div>
            )}
          </div>
        )}
      </Sheet.Body>

      {/* Bulk add action */}
      <Sheet.Footer>
        <Button
          type="contained"
          intent="secondary"
          size="md"
          label={t('addFromPool.addSelected', { count: selectedCount })}
          onClick={handleAddSelected}
          disabled={selectedCount === 0 || bulkAssign.isPending}
          loading={bulkAssign.isPending}
        />
      </Sheet.Footer>
    </Sheet>
  );
};

AddFromPoolModal.displayName = 'AddFromPoolModal';
